/**
 * Cliente API de la Consulta de vecinos (módulo Comisión).
 *
 * Permite a la Comisión ubicar a un vecino buscando por patente, RUT,
 * nombre o identificador de recurso (llavero / control), y ver la ficha
 * completa de la casa. Solo lectura.
 *
 * Consume:
 *  - GET /admin/consulta-vecinos/?q=...     → casas coincidentes.
 *  - GET /admin/consulta-vecinos/{lote_id}  → ficha completa de la casa.
 *
 * El backend exige require_comision (el tesorero también accede porque su
 * rol incluye comisión, doctrina convención #14).
 */
import { apiFetch } from './client';
import type { CasaAnualResumen, EstadoCasa } from './casas-tesoreria';

export type MotivoMatch = 'casa' | 'recurso' | 'patente' | 'rut' | 'nombre';

export interface CoincidenciaCasa {
  lote_id: string;
  identificador: string;
  sector: string | null;
  titular_nombre: string | null;
  motivo_match: MotivoMatch;
  detalle_match: string | null;
}


/** Estado de la cuenta de portal de una persona (backend: PORTAL_* de
 *  `services/consulta_vecinos.py`). `cuenta_sin_ingresar` es el que
 *  importa operativamente: la comisión aprobó, la cuenta existe y el
 *  vecino nunca entró. */
export type PortalEstado =
  | 'sin_solicitud'
  | 'solicitud_pendiente'
  | 'solicitud_rechazada'
  | 'aprobada_sin_cuenta'
  | 'cuenta_sin_ingresar'
  | 'cuenta_inactiva'
  | 'activo';

/** Bloques que van tanto en titulares como en otros residentes: los
 *  accesos cuelgan de `residentes` (incluida la fila del titular, que la
 *  ficha no lista aparte) y la cuenta de portal la puede tener el titular
 *  o el residente autorizado de la casa. */
export interface AccesosYPortal {
  portal_estado: PortalEstado;
  /** ISO. Sale de `auth.users.last_sign_in_at`, la misma fuente que el
   *  vecino ve en su home. */
  portal_ultimo_login: string | null;
  /** Slot 1-4. Los slots 1-2 reciben la llamada en cascada; 3-4 solo
   *  abren el portón desde el celular. */
  citofonia_slot: number | null;
  citofonia_estado: string | null;
  /** false = validado pero sin programar todavía en la central. */
  citofonia_programada: boolean;
  wifi_estado: string | null;
  wifi_programado: boolean;
  /** Estado que ve el usuario: cruce de autorizado / ejecutado /
   *  suspendido por morosidad. Es lo que hay que pintar. */
  wifi_estado_efectivo: string;
}

export interface PersonaFicha extends AccesosYPortal {
  persona_id: string;
  nombre_completo: string;
  rut: string | null;
  celular: string | null;
  email: string | null;
  tipo_tenencia: string;
  es_responsable_pago: boolean;
  /** Wizard de onboarding de la casa. Vive en la tenencia, por eso no
   *  aplica a los otros residentes. */
  onboarding_completado: boolean;
}

export interface ResidenteFicha extends AccesosYPortal {
  persona_id: string;
  nombre_completo: string;
  rut: string | null;
  celular: string | null;
  email: string | null;
  parentesco: string;
}

export interface VehiculoFicha {
  patente: string;
  marca: string | null;
  modelo: string | null;
  color: string | null;
  tipo_traccion: string;
  /** Control remoto que abre el portón para este vehículo + a quién se asignó. */
  control_remoto: string | null;
  asignado_a: string | null;
}

export interface RecursoFicha {
  tipo: string;
  identificador: string;
  estado: string;
  /** Serial físico impreso (RFID); solo llaveros. */
  serial: string | null;
  /** Residente que porta el recurso; null = en bolsa del lote (sin asignar). */
  persona_id: string | null;
  asignado_a: string | null;
  /** Para CR: patente del vehículo al que apunta. */
  vehiculo_patente: string | null;
}

export interface FichaVecino {
  // Reusa el resumen financiero de Tesorería (estado + saldos), solo lectura.
  casa: CasaAnualResumen;
  /** Estado de PAGO sin override de suspensión (al_dia / al_dia_pendiente_semestre / atrasada). */
  estado_pago: EstadoCasa;
  /** True si la casa tiene una suspensión vigente (se muestra ADEMÁS del estado de pago). */
  suspendida: boolean;
  personas: PersonaFicha[];
  residentes: ResidenteFicha[];
  vehiculos: VehiculoFicha[];
  recursos: RecursoFicha[];
}

export function buscarVecinos(q: string): Promise<CoincidenciaCasa[]> {
  return apiFetch<CoincidenciaCasa[]>(
    `/admin/consulta-vecinos/?q=${encodeURIComponent(q)}`,
  );
}

export function getFichaVecino(loteId: string): Promise<FichaVecino> {
  return apiFetch<FichaVecino>(`/admin/consulta-vecinos/${loteId}`);
}
